/**
 * Thin orchestrator client. Talks to the Fastify server at the CRA dev proxy
 * (configured via `proxy` field in root package.json) in dev, or to
 * `REACT_APP_API_BASE` in production.
 */
import { Project, FactoryConfig, AssemblyLine, AssemblyLineSummary, ReviewComment, PublishedArtifactInfo, Guardrail, Attachment, AttachmentKind } from '../types';
import type { AppImportSpec } from './appIo';
import { getToken, clearToken } from './auth';

// All API routes are namespaced under /api (so they don't collide with SPA
// routes). REACT_APP_API_BASE, when set, points at the API origin; otherwise
// we use a same-origin relative path.
const API_ORIGIN =
  (process.env.REACT_APP_API_BASE && process.env.REACT_APP_API_BASE.replace(/\/$/, '')) || '';
const BASE = `${API_ORIGIN}/api`;

/**
 * fetch wrapper that attaches the session Bearer token to every request and
 * handles expired/invalid sessions centrally: a 401 clears the stored token
 * and bounces the user to /login. All app + LLM calls below go through this.
 */
async function authedFetch(input: string, init: RequestInit = {}): Promise<Response> {
  const token = getToken();
  const headers = new Headers(init.headers || {});
  if (token) headers.set('Authorization', `Bearer ${token}`);
  const res = await fetch(input, { ...init, headers });
  if (res.status === 401) {
    clearToken();
    if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
      window.location.assign('/login');
    }
  } else if (res.status === 403) {
    // A 403 in the middle of a session = either the user was just deactivated
    // or their workspace was suspended. We peek at the body (cloning so the
    // caller can still read it) and bounce to /suspended in either case so
    // the in-app state isn't left half-rendered with broken sub-requests.
    try {
      const clone = res.clone();
      const body = (await clone.json()) as { error?: string };
      if (body?.error === 'workspace_suspended' || body?.error === 'inactive') {
        clearToken();
        if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/suspended')) {
          window.location.assign('/suspended');
        }
      }
    } catch {
      /* body wasn't JSON — let the caller handle */
    }
  }
  return res;
}

export interface BootstrapBody {
  idea: string;
  metadata?: Record<string, string>;
  assemblyLineId?: string;
  appName?: string;
  confluencePageUrl?: string;
  /**
   * Per-run step selection. When present, steps whose key isn't listed are
   * created 'skipped' so the run only executes the chosen phases/steps.
   */
  selectedStepKeys?: string[];
  /**
   * Files the user attached to the idea box. Text kinds are inlined into the
   * bootstrap prompt; images/other are stored and previewed only.
   */
  attachments?: Attachment[];
}

export interface ProjectListItem {
  id: string;
  appName: string;
  metadata: Record<string, string>;
  idea: string;
  currentPhase: string | null;
  updatedAt: string;
  createdAt: string;
  assemblyLineId: string | null;
  assemblyLineName: string | null;
  /** Control-plane user who created the project. Null on legacy rows. */
  ownerUserId: string | null;
  ownerName: string | null;
  ownerEmail: string | null;
  /** The current user's effective role on this project. */
  role: 'owner' | 'editor' | 'viewer' | null;
  /** True when this project was shared with the current user (they aren't the owner). */
  isShared: boolean;
  /** Number of phases where all steps are done/skipped. */
  donePhases?: number;
  /** Total number of phases. */
  totalPhases?: number;
}

export async function bootstrap(body: BootstrapBody): Promise<{ project: Project; warning?: string }> {
  const res = await authedFetch(`${BASE}/projects`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.text();
    throw new Error(`bootstrap failed (${res.status}): ${err}`);
  }
  const json = (await res.json()) as { project: Project; warning?: string };
  return { project: json.project, warning: json.warning };
}

export async function listProjects(): Promise<ProjectListItem[]> {
  const res = await authedFetch(`${BASE}/projects`);
  if (!res.ok) throw new Error(`listProjects failed (${res.status})`);
  const json = (await res.json()) as { projects: ProjectListItem[] };
  return json.projects;
}

export interface GetProjectResult {
  project: Project;
  activeStepVersions: Record<string, string>;
  /** The current user's effective role on this project. */
  role: 'owner' | 'editor' | 'viewer' | null;
}

export async function getProject(id: string): Promise<GetProjectResult> {
  const res = await authedFetch(`${BASE}/projects/${id}`);
  if (!res.ok) throw new Error(`getProject failed (${res.status})`);
  const json = (await res.json()) as {
    project: Project;
    activeStepVersions?: Record<string, string>;
    role?: 'owner' | 'editor' | 'viewer' | null;
  };
  return { project: json.project, activeStepVersions: json.activeStepVersions ?? {}, role: json.role ?? null };
}

export async function deleteProject(id: string): Promise<void> {
  const res = await authedFetch(`${BASE}/projects/${id}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(`deleteProject failed (${res.status})`);
}

/**
 * Import an app from an exported archive's portable spec. The server mints a
 * fresh id and persists it as a brand-new project owned by the current user.
 */
export async function importProject(spec: AppImportSpec): Promise<Project> {
  const res = await authedFetch(`${BASE}/projects/import`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ app: spec }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
    throw new Error(body.message || body.error || `import failed (${res.status})`);
  }
  const json = (await res.json()) as { project: Project };
  return json.project;
}

/**
 * Effective LLM provider/model for the current workspace. Tenant-scoped, so this
 * reflects the provider the admin saved in Settings. `fixtures` is true for
 * fixture-backed showcase workspaces (no live LLM).
 */
export async function llmProvider(): Promise<{ provider: string; model: string; fixtures: boolean }> {
  const res = await authedFetch(`${BASE}/llm/provider`);
  if (!res.ok) throw new Error(`llmProvider failed (${res.status})`);
  return res.json();
}

export async function getFactoryConfig(): Promise<FactoryConfig> {
  const res = await authedFetch(`${BASE}/config`);
  if (!res.ok) throw new Error(`getFactoryConfig failed (${res.status})`);
  return res.json();
}

export async function getDemoProject(): Promise<{
  project: Project;
  confluenceRefs: Record<string, PublishedArtifactInfo[]> | null;
}> {
  const res = await authedFetch(`${BASE}/projects/demo`);
  if (!res.ok) throw new Error(`getDemoProject failed (${res.status})`);
  const json = (await res.json()) as {
    project: Project;
    confluenceRefs?: Record<string, PublishedArtifactInfo[]> | null;
  };
  return { project: json.project, confluenceRefs: json.confluenceRefs ?? null };
}

export async function syncProjectToConfluence(
  projectId: string,
  opts?: { step?: string; artifact?: string },
): Promise<{
  published: number;
  skipped: number;
  publishedDocs: Record<string, PublishedArtifactInfo[]>;
}> {
  // Pass ?step=/?artifact= to publish only that step/artifact instead of the
  // whole app. Omitting both syncs everything (the app-level button).
  const qs = new URLSearchParams();
  if (opts?.step) qs.set('step', opts.step);
  if (opts?.artifact) qs.set('artifact', opts.artifact);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const res = await authedFetch(`${BASE}/projects/${projectId}/sync-confluence${suffix}`, { method: 'POST' });
  if (!res.ok) {
    let msg = `syncProjectToConfluence failed (${res.status})`;
    try { const j = await res.json(); if (j?.error) msg = j.error; } catch { /* ignore */ }
    throw new Error(msg);
  }
  return res.json();
}

/** Publish a single step's artifacts to its resolved Confluence connection. */
export async function syncStepToConfluence(
  projectId: string,
  stepKey: string,
): Promise<{
  published: number;
  skipped: number;
  publishedDocs: Record<string, PublishedArtifactInfo[]>;
}> {
  return syncProjectToConfluence(projectId, { step: stepKey });
}

/* --------------------------- Git host (repo) sync --------------------------- */

export interface RepoSyncResult {
  ok: true;
  commitSha: string;
  branch: string;
  changed: number;
  htmlUrl: string;
  pr?: { number: number; url: string };
}

/** Push the project's whole app tree to its resolved git connection. */
export async function syncProjectToRepo(
  projectId: string,
  opts?: { branch?: string; openPr?: boolean; message?: string; stepScoped?: boolean },
): Promise<RepoSyncResult> {
  const res = await authedFetch(`${BASE}/projects/${projectId}/sync-repo`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(opts ?? {}),
  });
  if (!res.ok) {
    let msg = `syncProjectToRepo failed (${res.status})`;
    try { const j = await res.json(); if (j?.error) msg = j.error; } catch { /* ignore */ }
    throw new Error(msg);
  }
  return res.json();
}

export interface RepoPullResult {
  ok: true;
  changed: number;
  ignored: number;
  headSha: string;
  branch: string;
  versionId?: string;
}

/** Pull the project's tree from its git connection into new artifact versions. */
export async function pullProjectFromRepo(
  projectId: string,
  opts?: { branch?: string },
): Promise<RepoPullResult> {
  const res = await authedFetch(`${BASE}/projects/${projectId}/pull-repo`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(opts ?? {}),
  });
  if (!res.ok) {
    let msg = `pullProjectFromRepo failed (${res.status})`;
    try { const j = await res.json(); if (j?.error) msg = j.error; } catch { /* ignore */ }
    throw new Error(msg);
  }
  return res.json();
}

export interface RepoImportResult {
  ok: true;
  projectId: string;
  appName: string;
  artifacts: number;
  foreign: boolean;
  headSha: string;
}

/** Import a git repository as a brand-new app-factory project. */
export async function importProjectFromRepo(opts: {
  profileId?: string;
  repo?: string;
  branch?: string;
}): Promise<RepoImportResult> {
  const res = await authedFetch(`${BASE}/projects/import-repo`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(opts),
  });
  if (!res.ok) {
    let msg = `importProjectFromRepo failed (${res.status})`;
    try { const j = await res.json(); if (j?.error) msg = j.error; } catch { /* ignore */ }
    throw new Error(msg);
  }
  return res.json();
}

export interface RepoStatus {
  connected: boolean;
  profile?: { id: string; name: string; host: string; workspace: string; repo: string };
  lastCommit?: { sha: string; branch: string; url: string };
  openPr?: { number: number; url: string; state: string };
}

export async function getProjectRepoStatus(projectId: string): Promise<RepoStatus> {
  const res = await authedFetch(`${BASE}/projects/${projectId}/repo-status`);
  if (!res.ok) throw new Error(`getProjectRepoStatus failed (${res.status})`);
  return res.json();
}

export interface ProjectRepoProfileOption {
  id: string;
  name: string;
  host: string;
  workspace: string;
  repo: string;
  isDefault: boolean;
  hasToken: boolean;
}

export interface ProjectRepoProfile {
  selectedProfileId: string | null;
  effectiveProfileId: string | null;
  profiles: ProjectRepoProfileOption[];
}

export async function getProjectRepoProfile(projectId: string): Promise<ProjectRepoProfile> {
  const res = await authedFetch(`${BASE}/projects/${projectId}/repo-profile`);
  if (!res.ok) throw new Error(`getProjectRepoProfile failed (${res.status})`);
  return res.json();
}

export async function setProjectRepoProfile(
  projectId: string,
  profileId: string | null,
): Promise<{ selectedProfileId: string | null; effectiveProfileId: string | null }> {
  const res = await authedFetch(`${BASE}/projects/${projectId}/repo-profile`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ profileId }),
  });
  if (!res.ok) {
    let msg = `setProjectRepoProfile failed (${res.status})`;
    try { const j = await res.json(); if (j?.error) msg = j.error; } catch { /* ignore */ }
    throw new Error(msg);
  }
  return res.json();
}

export interface StepRepoProfile {
  stepProfileId: string | null;
  projectProfileId: string | null;
  effectiveProfileId: string | null;
  stepSyncEnabled: boolean;
  profiles: ProjectRepoProfileOption[];
}

export async function getStepRepoProfile(projectId: string, stepKey: string): Promise<StepRepoProfile> {
  const res = await authedFetch(`${BASE}/projects/${projectId}/steps/${stepKey}/repo-profile`);
  if (!res.ok) throw new Error(`getStepRepoProfile failed (${res.status})`);
  return res.json();
}

export async function setStepRepoProfile(
  projectId: string,
  stepKey: string,
  profileId: string | null,
): Promise<{ stepProfileId: string | null; effectiveProfileId: string | null }> {
  const res = await authedFetch(`${BASE}/projects/${projectId}/steps/${stepKey}/repo-profile`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ profileId }),
  });
  if (!res.ok) {
    let msg = `setStepRepoProfile failed (${res.status})`;
    try { const j = await res.json(); if (j?.error) msg = j.error; } catch { /* ignore */ }
    throw new Error(msg);
  }
  return res.json();
}

/** Push a single step's artifacts to its resolved git connection. */
export async function syncStepToRepo(
  projectId: string,
  stepKey: string,
  opts?: { branch?: string; openPr?: boolean; message?: string },
): Promise<RepoSyncResult> {
  const res = await authedFetch(`${BASE}/projects/${projectId}/steps/${stepKey}/sync-repo`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(opts ?? {}),
  });
  if (!res.ok) {
    let msg = `syncStepToRepo failed (${res.status})`;
    try { const j = await res.json(); if (j?.error) msg = j.error; } catch { /* ignore */ }
    throw new Error(msg);
  }
  return res.json();
}

export interface RepoComment {
  id: string;
  path?: string;
  line?: number;
  body: string;
  author: string;
  createdAt: string;
}

/** A workspace Confluence connection, as exposed to the app screen's picker. */
export interface ProjectConfluenceProfileOption {
  id: string;
  name: string;
  siteUrl: string;
  confluenceSpace: string;
  jiraProject: string;
  isDefault: boolean;
  hasToken: boolean;
}

export interface ProjectConfluenceProfile {
  /** The app's explicit selection, or null when it follows the workspace default. */
  selectedProfileId: string | null;
  /** The connection it actually resolves to (selection, else default), or null. */
  effectiveProfileId: string | null;
  profiles: ProjectConfluenceProfileOption[];
}

export async function getProjectConfluenceProfile(projectId: string): Promise<ProjectConfluenceProfile> {
  const res = await authedFetch(`${BASE}/projects/${projectId}/confluence-profile`);
  if (!res.ok) throw new Error(`getProjectConfluenceProfile failed (${res.status})`);
  return res.json();
}

/** Set (or clear with null) which connection this app publishes to. */
export async function setProjectConfluenceProfile(
  projectId: string,
  profileId: string | null,
): Promise<{
  selectedProfileId: string | null;
  effectiveProfileId: string | null;
  /** Recomputed per-step active-version pointers after inheriting steps were realigned to the new profile. */
  activeStepVersions: Record<string, string>;
}> {
  const res = await authedFetch(`${BASE}/projects/${projectId}/confluence-profile`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ profileId }),
  });
  if (!res.ok) {
    let msg = `setProjectConfluenceProfile failed (${res.status})`;
    try { const j = await res.json(); if (j?.error) msg = j.error; } catch { /* ignore */ }
    throw new Error(msg);
  }
  return res.json();
}

export interface StepConfluenceProfile {
  /** This step's own override, or null when it inherits the app/default. */
  stepProfileId: string | null;
  /** The app-level selection (shown as the "inherit" target). */
  projectProfileId: string | null;
  /** The connection the step actually resolves to (step -> app -> default). */
  effectiveProfileId: string | null;
  /** Whether the step's assemblyLine enables the in-step sync control (live). */
  stepSyncEnabled: boolean;
  profiles: ProjectConfluenceProfileOption[];
}

export async function getStepConfluenceProfile(projectId: string, stepKey: string): Promise<StepConfluenceProfile> {
  const res = await authedFetch(`${BASE}/projects/${projectId}/steps/${stepKey}/confluence-profile`);
  if (!res.ok) throw new Error(`getStepConfluenceProfile failed (${res.status})`);
  return res.json();
}

/** Set (or clear with null = inherit) which connection one step publishes to. */
export async function setStepConfluenceProfile(
  projectId: string,
  stepKey: string,
  profileId: string | null,
): Promise<{ stepProfileId: string | null; effectiveProfileId: string | null }> {
  const res = await authedFetch(`${BASE}/projects/${projectId}/steps/${stepKey}/confluence-profile`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ profileId }),
  });
  if (!res.ok) {
    let msg = `setStepConfluenceProfile failed (${res.status})`;
    try { const j = await res.json(); if (j?.error) msg = j.error; } catch { /* ignore */ }
    throw new Error(msg);
  }
  return res.json();
}

export async function runStep(projectId: string, stepKey: string): Promise<void> {
  const res = await authedFetch(`${BASE}/projects/${projectId}/steps/${stepKey}/run`, {
    method: 'POST',
  });
  if (!res.ok && res.status !== 202) throw new Error(`runStep failed (${res.status})`);
}

/**
 * Ask the orchestrator to stop an in-flight run/retry for a step. 404 means
 * nothing is currently running (e.g. the run already finished) — not an error
 * worth surfacing, so we swallow it.
 */
export async function stopStep(projectId: string, stepKey: string): Promise<boolean> {
  const res = await authedFetch(`${BASE}/projects/${projectId}/steps/${stepKey}/stop`, {
    method: 'POST',
  });
  if (res.status === 404) return false;
  if (!res.ok) throw new Error(`stopStep failed (${res.status})`);
  return true;
}

/**
 * Skip a step without running it. The orchestrator marks it `skipped` and
 * returns the updated project. A 409 means a run is still in flight (stop it
 * first) — surface its message.
 */
export async function skipStep(projectId: string, stepKey: string): Promise<Project> {
  const res = await authedFetch(`${BASE}/projects/${projectId}/steps/${stepKey}/skip`, {
    method: 'POST',
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
    throw new Error(body.message || body.error || `skipStep failed (${res.status})`);
  }
  const json = (await res.json()) as { project: Project };
  return json.project;
}

export function openProjectStream(projectId: string): EventSource {
  // EventSource doesn't go through CRA's `proxy` field in dev. Use absolute URL.
  // We hardcode 127.0.0.1 (not window.location.hostname) because the orchestrator
  // binds to IPv4 only (API_HOST=127.0.0.1). On macOS, `localhost` may resolve
  // to ::1 first, which would silently fail and leave steps stuck in-progress.
  // In dev, CRA serves the UI on :3000 but the API (and SSE) live on :4000, and
  // EventSource bypasses CRA's proxy — so target :4000 directly. In production
  // the SPA is served same-origin by the orchestrator, so use the page origin.
  let origin = API_ORIGIN;
  if (!origin) {
    origin = window.location.port === '3000' ? 'http://127.0.0.1:4000' : window.location.origin;
  }
  // EventSource can't set an Authorization header, so the session token rides
  // along as a query param. The orchestrator's auth middleware accepts ?token.
  const token = getToken();
  const qs = token ? `?token=${encodeURIComponent(token)}` : '';
  return new EventSource(`${origin}/api/projects/${projectId}/stream${qs}`);
}

export interface StepHistoryEntry {
  versionId: string;
  createdAt: string;
  cause: string;
  version: number;
  artifacts: Array<{ name: string; content: string }>;
  /** User-supplied text that drove this version (initial idea, improvement feedback, or answers). Null when none. */
  prompt: string | null;
  /** Who produced this step version. Null on legacy/CLI rows. */
  actorUserId?: string | null;
  /** Display name (or email) of the actor. */
  actorName?: string | null;
}

export interface StepHistoryResult {
  history: StepHistoryEntry[];
  activeVersionId: string | null;
}

export async function getStepHistory(projectId: string, stepKey: string): Promise<StepHistoryResult> {
  const res = await authedFetch(`${BASE}/projects/${projectId}/steps/${stepKey}/history`);
  if (!res.ok) throw new Error(`getStepHistory failed (${res.status})`);
  const json = (await res.json()) as { history: StepHistoryEntry[]; activeVersionId?: string | null };
  return { history: json.history, activeVersionId: json.activeVersionId ?? null };
}

export interface FullPromptVersion {
  versionId: string;
  version: number;
  cause: string;
  createdAt: string;
  prompt: string | null;
}

export interface FullPromptStep {
  key: string;
  label: string;
  phaseKey: string;
  phaseLabel: string;
  versions: FullPromptVersion[];
}

export interface FullPromptHistory {
  project: { id: string; appName: string; idea: string; createdAt: string };
  steps: FullPromptStep[];
}

export async function getFullPromptHistory(projectId: string): Promise<FullPromptHistory> {
  const res = await authedFetch(`${BASE}/projects/${projectId}/full-history`);
  if (!res.ok) throw new Error(`getFullPromptHistory failed (${res.status})`);
  return (await res.json()) as FullPromptHistory;
}

export async function restoreStepFromVersion(
  projectId: string,
  stepKey: string,
  versionId: string,
): Promise<{ project: Project; activeVersionId: string; activeStepVersions: Record<string, string> }> {
  const res = await fetch(
    `${BASE}/projects/${projectId}/steps/${stepKey}/restore/${versionId}`,
    { method: 'POST' },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as { error?: string }).error || `restoreStepFromVersion failed (${res.status})`);
  }
  const json = (await res.json()) as {
    project: Project;
    activeVersionId: string;
    activeStepVersions?: Record<string, string>;
  };
  return {
    project: json.project,
    activeVersionId: json.activeVersionId,
    activeStepVersions: json.activeStepVersions ?? {},
  };
}

export async function editStep(
  projectId: string,
  stepKey: string,
  feedback: string,
  attachments?: Attachment[],
): Promise<void> {
  const res = await authedFetch(`${BASE}/projects/${projectId}/steps/${stepKey}/edit`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ feedback, attachments }),
  });
  if (!res.ok) throw new Error(`editStep failed (${res.status})`);
}

/**
 * Replace a single artifact's content from an uploaded file. Returns the
 * updated project so the store can patch its local state in place.
 */
export async function replaceArtifact(
  projectId: string,
  stepKey: string,
  name: string,
  content: string,
): Promise<Project> {
  const res = await authedFetch(`${BASE}/projects/${projectId}/steps/${stepKey}/artifacts`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, content }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
    throw new Error(body.message || body.error || `replaceArtifact failed (${res.status})`);
  }
  const json = (await res.json()) as { project: Project };
  return json.project;
}

export async function answerStep(
  projectId: string,
  stepKey: string,
  answers: Record<string, string>,
  attachments?: Attachment[],
): Promise<void> {
  const res = await authedFetch(`${BASE}/projects/${projectId}/steps/${stepKey}/answer`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ answers, attachments }),
  });
  if (!res.ok && res.status !== 202) throw new Error(`answerStep failed (${res.status})`);
}

/** Metadata for a persisted attachment (no bytes / extracted text). */
export interface PersistedAttachment {
  id: string;
  projectId: string;
  stepKey: string | null;
  name: string;
  mimeType: string;
  size: number;
  kind: AttachmentKind;
  hasText: boolean;
  createdAt: string;
}

/** List persisted attachment metadata for a project (idea-box + all steps). */
export async function listAttachments(projectId: string): Promise<PersistedAttachment[]> {
  const res = await authedFetch(`${BASE}/projects/${projectId}/attachments`);
  if (!res.ok) throw new Error(`listAttachments failed (${res.status})`);
  const json = (await res.json()) as { attachments: PersistedAttachment[] };
  return json.attachments ?? [];
}

/**
 * Build a download URL for one persisted attachment. The token rides in the
 * query string (not a header) because `<a href>`/`<img src>` navigations can't
 * set an Authorization header — the same reason the SSE endpoint accepts ?token=.
 */
export function attachmentDownloadUrl(projectId: string, attId: string): string {
  const token = getToken();
  const q = token ? `?token=${encodeURIComponent(token)}` : '';
  return `${BASE}/projects/${projectId}/attachments/${encodeURIComponent(attId)}${q}`;
}

export interface ImpactRow {
  stepKey: string;
  severity: 'none' | 'soft' | 'hard';
  reason: string;
  artifactsToRederive?: string[];
}

export interface UsageTotals {
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheCreationTokens: number;
  costUsd: number;
}

export async function projectUsage(projectId: string): Promise<UsageTotals> {
  const res = await authedFetch(`${BASE}/projects/${projectId}/usage`);
  if (!res.ok) throw new Error(`projectUsage failed (${res.status})`);
  return res.json();
}

export async function pinStep(projectId: string, stepKey: string, pin: boolean): Promise<void> {
  const res = await authedFetch(`${BASE}/projects/${projectId}/steps/${stepKey}/pin`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pin }),
  });
  if (!res.ok) throw new Error(`pinStep failed (${res.status})`);
}

/* ------------------------------------------------------------------ */
/*  Collaboration API                                                 */
/* ------------------------------------------------------------------ */

export interface WorkspaceMember {
  id: string;
  name: string | null;
  email: string;
  role: 'super_admin' | 'enterprise_admin' | 'enterprise_user';
}

export interface Collaborator {
  userId: string;
  role: 'editor' | 'viewer';
  addedBy: string;
  createdAt: string;
  name: string | null;
  email: string | null;
}

export interface ProjectCollaborators {
  owner: { userId: string; name: string | null; email: string } | null;
  collaborators: Collaborator[];
}

export interface ActivityEntry {
  id: number;
  ts: string;
  kind: string;
  stepKey: string | null;
  cause: string | null;
  versionId: string | null;
  actorUserId: string | null;
  actorName: string | null;
}

/** Active members of the current user's workspace, for the share picker. */
export async function getWorkspaceMembers(): Promise<WorkspaceMember[]> {
  const res = await authedFetch(`${BASE}/enterprise/members`);
  if (!res.ok) throw new Error(`getWorkspaceMembers failed (${res.status})`);
  const json = (await res.json()) as { members: WorkspaceMember[] };
  return json.members;
}

export async function getCollaborators(projectId: string): Promise<ProjectCollaborators> {
  const res = await authedFetch(`${BASE}/projects/${projectId}/collaborators`);
  if (!res.ok) throw new Error(`getCollaborators failed (${res.status})`);
  return (await res.json()) as ProjectCollaborators;
}

export async function addCollaborator(
  projectId: string,
  userId: string,
  role: 'editor' | 'viewer',
): Promise<Collaborator[]> {
  const res = await authedFetch(`${BASE}/projects/${projectId}/collaborators`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId, role }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as { message?: string }).message || `addCollaborator failed (${res.status})`);
  }
  const json = (await res.json()) as { collaborators: Collaborator[] };
  return json.collaborators;
}

export async function removeCollaborator(projectId: string, userId: string): Promise<Collaborator[]> {
  const res = await authedFetch(`${BASE}/projects/${projectId}/collaborators/${userId}`, {
    method: 'DELETE',
  });
  if (!res.ok) throw new Error(`removeCollaborator failed (${res.status})`);
  const json = (await res.json()) as { collaborators: Collaborator[] };
  return json.collaborators;
}

export async function transferOwner(projectId: string, userId: string): Promise<void> {
  const res = await authedFetch(`${BASE}/projects/${projectId}/transfer-owner`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as { message?: string }).message || `transferOwner failed (${res.status})`);
  }
}

export async function getProjectActivity(projectId: string): Promise<ActivityEntry[]> {
  const res = await authedFetch(`${BASE}/projects/${projectId}/activity`);
  if (!res.ok) throw new Error(`getProjectActivity failed (${res.status})`);
  const json = (await res.json()) as { activity: ActivityEntry[] };
  return json.activity;
}

/* ------------------------------------------------------------------ */
/*  AssemblyLine API                                                        */
/* ------------------------------------------------------------------ */

export async function listAssemblyLines(): Promise<AssemblyLineSummary[]> {
  const res = await authedFetch(`${BASE}/assembly-lines`);
  if (!res.ok) throw new Error(`listAssemblyLines failed (${res.status})`);
  const json = (await res.json()) as { assemblyLines: AssemblyLineSummary[] };
  return json.assemblyLines;
}

export async function getAssemblyLine(id: string): Promise<AssemblyLine> {
  const res = await authedFetch(`${BASE}/assembly-lines/${id}`);
  if (!res.ok) throw new Error(`getAssemblyLine failed (${res.status})`);
  const json = (await res.json()) as { assemblyLine: AssemblyLine };
  return json.assemblyLine;
}

export async function createAssemblyLine(data: Partial<AssemblyLine> & { name: string }): Promise<AssemblyLine> {
  const res = await authedFetch(`${BASE}/assembly-lines`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.text();
    throw new Error(`createAssemblyLine failed (${res.status}): ${err}`);
  }
  const json = (await res.json()) as { assemblyLine: AssemblyLine };
  return json.assemblyLine;
}

export async function updateAssemblyLine(id: string, data: Partial<AssemblyLine> & { name: string }): Promise<AssemblyLine> {
  const res = await authedFetch(`${BASE}/assembly-lines/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.text();
    throw new Error(`updateAssemblyLine failed (${res.status}): ${err}`);
  }
  const json = (await res.json()) as { assemblyLine: AssemblyLine };
  return json.assemblyLine;
}

export async function deleteAssemblyLine(id: string): Promise<void> {
  const res = await authedFetch(`${BASE}/assembly-lines/${id}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(`deleteAssemblyLine failed (${res.status})`);
}

/* ------------------------------------------------------------------ */
/*  Review comments                                                    */
/* ------------------------------------------------------------------ */

export async function getPublishedDocs(
  projectId: string,
  stepKey: string,
): Promise<{ docs: PublishedArtifactInfo[]; provider: string | null }> {
  const res = await authedFetch(
    `${BASE}/projects/${projectId}/steps/${stepKey}/published-docs`,
  );
  if (!res.ok) return { docs: [], provider: null };
  return (await res.json()) as { docs: PublishedArtifactInfo[]; provider: string | null };
}

export async function resolveComments(
  projectId: string,
  commentIds: string[],
  resolutionMessage?: string,
  stepKey?: string,
): Promise<{ resolved: string[]; failed: string[] }> {
  const res = await authedFetch(`${BASE}/projects/${projectId}/comments/resolve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ commentIds, resolutionMessage, stepKey }),
  });
  if (!res.ok) return { resolved: [], failed: commentIds };
  return (await res.json()) as { resolved: string[]; failed: string[] };
}

/**
 * Apply reviewer comments: the backend surgically edits the published document
 * (preserving inline comment markers), syncs the source artifact, replies to
 * each comment, and resolves it. Returns the updated docs and resolved IDs.
 */
export async function applyComments(
  projectId: string,
  stepKey: string,
  commentIds: string[],
): Promise<{ docs: PublishedArtifactInfo[]; resolved: string[]; failed: string[] }> {
  const res = await authedFetch(`${BASE}/projects/${projectId}/comments/apply`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ stepKey, commentIds }),
  });
  if (!res.ok) return { docs: [], resolved: [], failed: commentIds };
  return (await res.json()) as { docs: PublishedArtifactInfo[]; resolved: string[]; failed: string[] };
}

export async function getStepComments(
  projectId: string,
  stepKey: string,
): Promise<ReviewComment[]> {
  const res = await authedFetch(
    `${BASE}/projects/${projectId}/steps/${stepKey}/comments`,
  );
  if (!res.ok) return []; // graceful degradation
  const json = (await res.json()) as { comments: ReviewComment[] };
  return json.comments;
}

/* ------------------------------------------------------------------ */
/*  Demo build                                                        */
/* ------------------------------------------------------------------ */

export interface DemoBuildResult {
  ok: true;
  projectId: string;
  fileCount: number;
  bytes: number;
}

export async function postDemoBuild(
  projectId: string,
  files: Record<string, string>,
): Promise<DemoBuildResult> {
  const res = await authedFetch(`${BASE}/projects/${projectId}/demo-build`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ files }),
  });
  if (!res.ok) {
    const err = await res.text();
    throw new Error(`postDemoBuild failed (${res.status}): ${err}`);
  }
  return res.json();
}

/** Stable URL for the persisted demo iframe. */
export function demoUrl(projectId: string): string {
  return `${BASE}/projects/${projectId}/demo/`;
}

/* ------------------------------------------------------------------ */
/*  Skills API                                                        */
/* ------------------------------------------------------------------ */

export interface SkillInfo {
  name: string;
  description: string;
  contract: string;
  available: boolean;
  isStub: boolean;
  hasFixture: boolean;
}

export async function listSkills(detailed = true): Promise<SkillInfo[]> {
  const res = await authedFetch(`${BASE}/skills?detailed=${detailed}`);
  if (!res.ok) throw new Error(`listSkills failed (${res.status})`);
  const json = (await res.json()) as { skills: SkillInfo[] };
  return json.skills;
}

/** Replace the app-level guardrails on a single step. Returns the updated project. */
export async function setStepGuardrails(
  projectId: string,
  stepKey: string,
  guardrails: Guardrail[],
): Promise<Project> {
  const res = await authedFetch(`${BASE}/projects/${projectId}/steps/${stepKey}/guardrails`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ guardrails }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as any)?.error ? `setStepGuardrails: ${(body as any).error}` : `setStepGuardrails failed (${res.status})`);
  }
  const json = (await res.json()) as { project: Project };
  return json.project;
}
